import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ATS_API_URL } from '../config';

export type DashboardFeatureFlags = {
  hideDashboardHudForSuperAdmin: boolean;
  showLastSubmittedDateColumn: boolean;
};

type FeatureFlagContextValue = {
  featureFlags: DashboardFeatureFlags;
  isLoading: boolean;
  error: Error | null;
  isFeatureEnabled: (flagName: keyof DashboardFeatureFlags) => boolean;
};

const DEFAULT_FEATURE_FLAGS: DashboardFeatureFlags = {
  hideDashboardHudForSuperAdmin: false,
  showLastSubmittedDateColumn: false,
};

const defaultContextValue: FeatureFlagContextValue = {
  featureFlags: DEFAULT_FEATURE_FLAGS,
  isLoading: false,
  error: null,
  isFeatureEnabled: () => false,
};

const FeatureFlagContext = createContext<FeatureFlagContextValue>(defaultContextValue);

type FeatureFlagProviderProps = {
  children: ReactNode;
};

function normalizeFeatureFlags(value: unknown): DashboardFeatureFlags {
  const flags = value && typeof value === 'object' ? value as Partial<DashboardFeatureFlags> : {};

  return {
    hideDashboardHudForSuperAdmin: flags.hideDashboardHudForSuperAdmin === true,
    showLastSubmittedDateColumn: flags.showLastSubmittedDateColumn === true,
  };
}

export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  const [featureFlags, setFeatureFlags] = useState<DashboardFeatureFlags>(DEFAULT_FEATURE_FLAGS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadFeatureFlags = async () => {
      if (!ATS_API_URL) {
        if (isMounted) {
          setFeatureFlags(DEFAULT_FEATURE_FLAGS);
          setError(new Error('ATS_API_URL is not configured.'));
          setIsLoading(false);
        }
        return;
      }

      try {
        const response = await fetch(`${ATS_API_URL}/dashboard/config`, {
          cache: 'no-store',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Dashboard config request failed with status ${response.status}`);
        }

        const payload = await response.json();
        if (isMounted) {
          setFeatureFlags(normalizeFeatureFlags(payload?.featureFlags));
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setFeatureFlags(DEFAULT_FEATURE_FLAGS);
          setError(err instanceof Error ? err : new Error('Dashboard config request failed.'));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadFeatureFlags();

    return () => {
      isMounted = false;
    };
  }, []);

  const isFeatureEnabled = useCallback(
    (flagName: keyof DashboardFeatureFlags) => featureFlags[flagName] === true,
    [featureFlags]
  );

  const value = useMemo(
    () => ({
      featureFlags,
      isLoading,
      error,
      isFeatureEnabled,
    }),
    [error, featureFlags, isFeatureEnabled, isLoading]
  );

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlags = () => useContext(FeatureFlagContext);

export { DEFAULT_FEATURE_FLAGS };
